import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  fetchPayers, createPayer, updatePayer, deletePayer,
  fetchPayerSourceMappings, createPayerSourceMapping, deletePayerSourceMapping,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useFacility } from '../contexts/FacilityContext';
import { AdminTableSkeleton } from '../components/Skeleton';
import './Admin.css';
import './Payers.css';

const ALL_FORMS = ['CMS-1500', 'CMS-1450'];
const EMPTY_FORM = {
  name: '', payer_code: '', address: '', city: '', state: '', zip: '', phone: '',
  accepted_form_types: [...ALL_FORMS],
};

// MySQL SET arrives as a comma string; normalize to an array, defaulting to both forms.
const parseForms = (val) => {
  if (Array.isArray(val)) return val.length ? val : [...ALL_FORMS];
  if (typeof val === 'string' && val.trim()) return val.split(',').map(s => s.trim()).filter(Boolean);
  return [...ALL_FORMS];
};

// ─────────────────────────────────────────────────────────────────────────────
// Source payer mappings (sync plan Step 11, S11-c) — admins only.
//
// A facility's source system (the RIS) sends ITS OWN identifier for each payer
// on a coverage record. Nothing it sends is ever billed: a row here is the only
// thing that turns that identifier into one of the payers above. An unmapped
// identifier is refused (422 UNKNOWN_PAYER), and the alert the source team
// receives names the identifier to add here.
//
// A change here governs the NEXT coverage delivery only. Coverage already stored
// keeps the payer it was mapped to, and so do claims.
// ─────────────────────────────────────────────────────────────────────────────
function SourcePayerMappings({ payers }) {
  const { facilities } = useFacility();
  const [facilityId, setFacilityId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ source_system: '', source_payer_identifier: '', payer_id: '' });
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = useCallback(async (id) => {
    if (!id) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPayerSourceMappings(id);
      setData(res.data);
      // Pre-fill the facility's configured source system: a mapping under any
      // other source system is one this facility's deliveries never match.
      setForm((f) => ({ ...f, source_system: res.data.configured_source_system || f.source_system }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load mappings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(facilityId); }, [facilityId, load]);

  const payerChoices = useMemo(() => payers.filter((p) => !p.is_self_pay_payer), [payers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.source_system.trim() || !form.source_payer_identifier.trim() || !form.payer_id) {
      setError('Source system, the source’s payer identifier and an RCM payer are all required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createPayerSourceMapping({
        facility_id: Number(facilityId),
        source_system: form.source_system.trim(),
        source_payer_identifier: form.source_payer_identifier.trim(),
        payer_id: Number(form.payer_id),
      });
      setForm((f) => ({ ...f, source_payer_identifier: '', payer_id: '' }));
      await load(facilityId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add mapping.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setSaving(true);
    try {
      await deletePayerSourceMapping(removeTarget.id);
      setRemoveTarget(null);
      await load(facilityId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove mapping.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="psm-section">
      <div className="psm-header">
        <div>
          <h2>Source payer mappings</h2>
          <p>
            Which payer a facility’s synced coverage bills. The source system’s own payer identifier is never
            placed on a claim — it is looked up here. A change applies to the next coverage it sends; stored
            coverage and claims keep the payer they already have.
          </p>
        </div>
        <select
          className="admin-modal-input psm-facility-select"
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
          aria-label="Facility"
        >
          <option value="">Choose a facility…</option>
          {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>

      {error && <div className="admin-modal-alert error psm-alert"><span>{error}</span></div>}

      {facilityId && (
        <div className="admin-table-card">
          {loading ? (
            <p className="psm-muted">Loading…</p>
          ) : (
            <>
              {data && !data.configured_source_system && (
                <p className="psm-muted psm-note">
                  This facility has no sync configuration yet, so no source system is known for it. Type the source
                  system exactly as its sync credential is configured.
                </p>
              )}
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Source system</th>
                    <th>Source payer identifier</th>
                    <th>Bills as (RCM payer)</th>
                    <th>Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.mappings ?? []).length === 0 && (
                    <tr><td colSpan={5} className="psm-muted">No mappings yet. Coverage naming any payer is refused until one is added.</td></tr>
                  )}
                  {(data?.mappings ?? []).map((m) => (
                    <tr key={m.id}>
                      <td>{m.source_system}</td>
                      <td><span className="payer-code-badge">{m.source_payer_identifier}</span></td>
                      <td>
                        {m.payer_name}
                        {m.payer_code && <span className="psm-muted"> · {m.payer_code}</span>}
                      </td>
                      <td>{m.created_at ? String(m.created_at).slice(0, 10) : '—'}</td>
                      <td>
                        <button className="admin-action-btn deactivate" onClick={() => setRemoveTarget(m)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <form className="psm-add" onSubmit={handleAdd}>
                <input
                  className="admin-modal-input"
                  placeholder="Source system"
                  aria-label="Source system"
                  value={form.source_system}
                  onChange={(e) => setForm((f) => ({ ...f, source_system: e.target.value }))}
                />
                <input
                  className="admin-modal-input"
                  placeholder="Source payer identifier, exactly as sent"
                  aria-label="Source payer identifier"
                  value={form.source_payer_identifier}
                  onChange={(e) => setForm((f) => ({ ...f, source_payer_identifier: e.target.value }))}
                />
                <select
                  className="admin-modal-input"
                  aria-label="RCM payer"
                  value={form.payer_id}
                  onChange={(e) => setForm((f) => ({ ...f, payer_id: e.target.value }))}
                >
                  <option value="">Bills as…</option>
                  {payerChoices.map((p) => <option key={p.payer_id} value={p.payer_id}>{p.name}</option>)}
                </select>
                <button type="submit" className="admin-create-btn" disabled={saving}>
                  {saving ? 'Saving…' : 'Add mapping'}
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {removeTarget && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Remove mapping?</h3>
            <p>
              <strong>{removeTarget.source_payer_identifier}</strong> will stop mapping to{' '}
              <strong>{removeTarget.payer_name}</strong>. Coverage already stored keeps that payer; the next
              coverage naming this identifier will be refused until it is mapped again.
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setRemoveTarget(null)} disabled={saving}>Cancel</button>
              <button className="admin-confirm-danger" onClick={handleRemove} disabled={saving}>
                {saving ? 'Removing…' : 'Remove mapping'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Payers() {
  const { user } = useAuth();
  const [payers, setPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadPayers = useCallback(async () => {
    try {
      const data = await fetchPayers();
      setPayers(data.data || []);
    } catch (err) {
      console.error('Failed to load payers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPayers(); }, [loadPayers]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return payers;
    return payers.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.payer_code || '').toLowerCase().includes(q) ||
      (p.city || '').toLowerCase().includes(q) ||
      (p.state || '').toLowerCase().includes(q)
    );
  }, [payers, searchQuery]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModalError(null);
    setModalMode('create');
  };

  const openEdit = (payer) => {
    setEditTarget(payer);
    setForm({
      name: payer.name || '',
      payer_code: payer.payer_code || '',
      address: payer.address || '',
      city: payer.city || '',
      state: payer.state || '',
      zip: payer.zip || '',
      phone: payer.phone || '',
      accepted_form_types: parseForms(payer.accepted_form_types),
    });
    setModalError(null);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setModalError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setModalError('Payer name is required.');
      return;
    }
    if (!form.accepted_form_types.length) {
      setModalError('Select at least one accepted claim form.');
      return;
    }
    setSaving(true);
    setModalError(null);
    try {
      if (modalMode === 'create') {
        const data = await createPayer(form);
        setPayers(prev => [...prev, data.data].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const data = await updatePayer(editTarget.payer_id, form);
        setPayers(prev => prev.map(p => p.payer_id === editTarget.payer_id ? data.data : p));
      }
      closeModal();
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setModalError('A payer with this name already exists.');
      } else {
        setModalError(err.response?.data?.error || 'Failed to save payer. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePayer(deleteTarget.payer_id);
      setPayers(prev => prev.filter(p => p.payer_id !== deleteTarget.payer_id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm(f => ({ ...f, [key]: e.target.value })),
  });

  if (loading) {
    return <AdminTableSkeleton columns={6} rows={6} />;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Insurance Payers</h1>
          <p>Manage your master payer list — link patients to payers from their insurance profile</p>
        </div>
        <button className="admin-create-btn" onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Payer
        </button>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search"
            placeholder="Search by name, EDI code, city, or state..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <span className="admin-count">
          {filtered.length} {filtered.length === 1 ? 'payer' : 'payers'}
        </span>
      </div>

      <div className="admin-table-card">
        {filtered.length === 0 ? (
          <div className="admin-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
            <h3>{searchQuery ? 'No payers match your search' : 'No payers yet'}</h3>
            <p>{searchQuery ? 'Try a different name or code' : 'Add your first payer to get started'}</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Payer Name</th>
                <th>EDI Payer Code</th>
                <th>Address</th>
                <th>Phone</th>
                <th>Linked Patients</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(payer => (
                <tr key={payer.payer_id}>
                  <td>
                    <div className="admin-name-cell">
                      <div className="admin-avatar payer-avatar">
                        {payer.name.slice(0, 2).toUpperCase()}
                      </div>
                      <span>{payer.name}</span>
                    </div>
                  </td>
                  <td>
                    {payer.payer_code
                      ? <span className="payer-code-badge">{payer.payer_code}</span>
                      : <span className="payer-no-value">—</span>}
                  </td>
                  <td className="payer-address-cell">
                    {payer.address || payer.city || payer.state ? (
                      <>
                        {payer.address && <span className="payer-address-line">{payer.address}</span>}
                        {(payer.city || payer.state || payer.zip) && (
                          <span className="payer-address-csz">
                            {[payer.city, payer.state].filter(Boolean).join(', ')}
                            {payer.zip ? ` ${payer.zip}` : ''}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="payer-no-value">—</span>
                    )}
                  </td>
                  <td>{payer.phone || <span className="payer-no-value">—</span>}</td>
                  <td>
                    <span className={`payer-patients-badge ${payer.patient_count > 0 ? 'has-patients' : ''}`}>
                      {payer.patient_count}
                    </span>
                  </td>
                  <td>
                    <div className="payer-actions">
                      <button className="admin-action-btn payer-edit-btn" onClick={() => openEdit(payer)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit
                      </button>
                      <button className="admin-action-btn deactivate" onClick={() => setDeleteTarget(payer)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Step 11 (S11-c): admin-only on the server too — this only hides it. */}
      {user?.role === 'admin' && <SourcePayerMappings payers={payers} />}

      {/* Add / Edit Modal */}
      {modalMode && (
        <div className="admin-modal-overlay">
          <div className="admin-modal payer-modal">
            <div className="admin-modal-header">
              <div className="admin-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              </div>
              <div className="admin-modal-header-text">
                <h2>{modalMode === 'create' ? 'Add Payer' : 'Edit Payer'}</h2>
                <p>{modalMode === 'create' ? 'Add a new insurance payer to the master list' : `Editing ${editTarget?.name}`}</p>
              </div>
            </div>

            {modalError && (
              <div className="admin-modal-alert error" style={{ marginBottom: 18 }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleSave} className="admin-modal-form">
              <div className="admin-modal-row">
                <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="payer-name">Payer Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    id="payer-name"
                    type="text"
                    className="admin-modal-input"
                    placeholder="e.g. Blue Cross Blue Shield"
                    autoFocus
                    {...field('name')}
                  />
                </div>
              </div>

              <div className="admin-modal-field">
                <label htmlFor="payer-code">EDI Payer Code</label>
                <input
                  id="payer-code"
                  type="text"
                  className="admin-modal-input"
                  placeholder="e.g. 00050"
                  {...field('payer_code')}
                />
              </div>

              <div className="admin-modal-field">
                <label htmlFor="payer-address">Street Address</label>
                <input
                  id="payer-address"
                  type="text"
                  className="admin-modal-input"
                  placeholder="123 Main St"
                  {...field('address')}
                />
              </div>

              <div className="admin-modal-row">
                <div className="admin-modal-field">
                  <label htmlFor="payer-city">City</label>
                  <input
                    id="payer-city"
                    type="text"
                    className="admin-modal-input"
                    placeholder="City"
                    {...field('city')}
                  />
                </div>
                <div className="admin-modal-field">
                  <label htmlFor="payer-state">State</label>
                  <input
                    id="payer-state"
                    type="text"
                    className="admin-modal-input"
                    placeholder="TX"
                    maxLength={2}
                    style={{ textTransform: 'uppercase' }}
                    {...field('state')}
                    onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase() }))}
                  />
                </div>
              </div>

              <div className="admin-modal-row">
                <div className="admin-modal-field">
                  <label htmlFor="payer-zip">ZIP Code</label>
                  <input
                    id="payer-zip"
                    type="text"
                    className="admin-modal-input"
                    placeholder="12345"
                    {...field('zip')}
                  />
                </div>
                <div className="admin-modal-field">
                  <label htmlFor="payer-phone">Phone</label>
                  <input
                    id="payer-phone"
                    type="text"
                    className="admin-modal-input"
                    placeholder="(800) 555-0100"
                    {...field('phone')}
                  />
                </div>
              </div>

              <div className="admin-modal-row">
                <div className="admin-modal-field">
                  <label>Accepted Claim Forms</label>
                  <div className="payer-forms-group">
                    {ALL_FORMS.map((ft) => {
                      const checked = form.accepted_form_types.includes(ft);
                      return (
                        <label key={ft} className="payer-form-checkbox">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setForm((f) => {
                              const next = checked
                                ? f.accepted_form_types.filter((x) => x !== ft)
                                : [...f.accepted_form_types, ft];
                              return { ...f, accepted_form_types: next };
                            })}
                          />
                          <span>{ft === 'CMS-1500' ? 'CMS-1500 (Professional)' : 'CMS-1450 (Institutional / UB-04)'}</span>
                        </label>
                      );
                    })}
                  </div>
                  <small className="payer-forms-hint">
                    Uncheck a form this payer rejects. A facility whose default form is unchecked here
                    will be switched to the accepted form at claim generation.
                  </small>
                </div>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-modal-btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-modal-btn-submit" disabled={saving}>
                  {saving && <span className="admin-modal-spinner" />}
                  {saving ? 'Saving...' : modalMode === 'create' ? 'Add Payer' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="admin-modal-overlay">
          <div className="admin-confirm-dialog">
            <h3>Delete Payer?</h3>
            <p>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
            </p>
            {deleteTarget.patient_count > 0 && (
              <div className="payer-delete-warning">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>
                  This payer is linked to <strong>{deleteTarget.patient_count}</strong> patient insurance {deleteTarget.patient_count === 1 ? 'record' : 'records'}.
                  Those records will be unlinked but not deleted.
                </span>
              </div>
            )}
            <div className="admin-confirm-actions">
              <button className="admin-modal-btn-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="admin-confirm-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete Payer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payers;
